import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { addDailyLoginReward, checkDailyRewardEligibility, getLoginStreak } from '../services/walletService';
import { collection, query, where, orderBy, limit, getDocs, Timestamp, getDoc, doc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useWallet } from '../context/WalletContext';

const ClaimDailyLoginButton = ({ onRewardClaimed }) => {
  const { currentUser } = useAuth();
  const { refreshWallet, wallets, isInitialized, coinSources } = useWallet();
  const [canClaimToday, setCanClaimToday] = useState(false); // Default to false until we check eligibility
  const [loading, setLoading] = useState(false);
  const [nextClaimTime, setNextClaimTime] = useState(null);
  const [checking, setChecking] = useState(true);
  const [lastClaimDate, setLastClaimDate] = useState(null);
  const [streak, setStreak] = useState({ currentStreak: 0, highestStreak: 0 });
  const [rewardEstimate] = useState(200); // Fixed reward amount
  const [lastRefreshTime, setLastRefreshTime] = useState(Date.now());
  const [claimSuccess, setClaimSuccess] = useState(false);
  const [refreshAttempts, setRefreshAttempts] = useState(0);

  // Ensure wallet is loaded first
  useEffect(() => {
    if (currentUser && isInitialized) {
      // Check if wallet needs refresh
      const shouldRefreshWallet = 
        !wallets || 
        (wallets.walletA === 0 && (coinSources.login > 0 || refreshAttempts === 0)) || // Force refresh if wallet appears to be empty
        Date.now() - lastRefreshTime > 60000; // Refresh if it's been more than a minute
      
      if (shouldRefreshWallet) {
        console.log("ClaimDailyLoginButton: Refreshing wallet");
        setRefreshAttempts(prev => prev + 1);
        
        refreshWallet().then(() => {
          setLastRefreshTime(Date.now());
        }).catch(err => {
          console.error("Error refreshing wallet:", err);
        });
      }
    }
  }, [currentUser, wallets, refreshWallet, isInitialized, coinSources, refreshAttempts]);

  // Check eligibility when component mounts or when user changes
  useEffect(() => {
    const checkEligibility = async () => {
      if (!currentUser || !isInitialized) {
        setChecking(false);
        setCanClaimToday(false);
        return;
      }

      try {
        setChecking(true);
        
        // Simplify streak handling to avoid permission errors
        try {
          // Get the user's current streak information
          const streakInfo = await getLoginStreak(currentUser.uid);
          console.log("Current streak info:", streakInfo);
          setStreak(streakInfo);
        } catch (error) {
          // If we can't get streak info, just use default values
          console.error("Error fetching streak info, using defaults:", error);
          setStreak({ currentStreak: 0, highestStreak: 0 });
        }
        
        // Check if user can claim today
        const isEligible = await checkDailyRewardEligibility(currentUser.uid);
        console.log("User eligible to claim:", isEligible);
        setCanClaimToday(isEligible);
        
        if (!isEligible) {
          // Set next claim time to midnight tomorrow
          const now = new Date();
          const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          const tomorrowStart = new Date(todayStart);
          tomorrowStart.setDate(tomorrowStart.getDate() + 1);
          setNextClaimTime(tomorrowStart);
          
          // Get last claim time from localStorage
          if (typeof window !== 'undefined' && window.localStorage) {
            const lastClaimStr = localStorage.getItem(`lastClaim_${currentUser.uid}`);
            if (lastClaimStr) {
              setLastClaimDate(new Date(parseInt(lastClaimStr)));
            }
          }
          
          // As a fallback, also try getting last claim from transactions
          try {
            const transactionsRef = collection(db, 'users', currentUser.uid, 'transactions');
            const rewardQuery = query(
              transactionsRef,
              where('type', '==', 'login_reward'),
              orderBy('timestamp', 'desc'),
              limit(1)
            );
            
            const rewardSnapshot = await getDocs(rewardQuery);
            
            if (!rewardSnapshot.empty) {
              const lastReward = rewardSnapshot.docs[0].data();
              if (lastReward.timestamp && !lastClaimDate) {
                setLastClaimDate(lastReward.timestamp.toDate());
              }
            }
          } catch (error) {
            console.error("Error getting transactions, continuing with localStorage date:", error);
            // Continue using the localStorage date if available
          }
        }
      } catch (error) {
        console.error("Error checking login rewards:", error);
        setCanClaimToday(false);
      } finally {
        setChecking(false);
      }
    };

    // Initialize checking
    if (isInitialized) {
      checkEligibility();
    }
    
    // Set up timer to re-check eligibility at midnight
    const setupMidnightRefresh = () => {
      const now = new Date();
      const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
      const timeUntilMidnight = tomorrow - now;
      
      const timer = setTimeout(() => {
        checkEligibility();
      }, timeUntilMidnight);
      
      return () => clearTimeout(timer);
    };
    
    const cleanup = setupMidnightRefresh();
    return cleanup;
  }, [currentUser, claimSuccess, isInitialized]);

  // Multiple wallet refresh function for reliable updating
  const refreshWalletMultipleTimes = async () => {
    try {
      // First refresh immediately
      console.log("First wallet refresh after claiming reward");
      await refreshWallet();
      setLastRefreshTime(Date.now());
      
      // Schedule additional refreshes with delays
      const refreshDelays = [1000, 3000, 5000];
      let walletAIncreased = false;
      let loginSourceIncreased = false;
      
      for (const [index, delay] of refreshDelays.entries()) {
        await new Promise(resolve => setTimeout(resolve, delay));
        console.log(`Additional wallet refresh #${index + 1} to ensure balance is updated`);
        await refreshWallet();
        
        // Check if login source has been updated properly
        if (coinSources.login > 0) {
          loginSourceIncreased = true;
        }
        
        // Check if walletA has been updated properly
        if (wallets.walletA > 0) {
          walletAIncreased = true;
        }
        
        // If both conditions are met, we can break early
        if (loginSourceIncreased && walletAIncreased) {
          console.log("Wallet and login source successfully updated");
          break;
        }
      }
      
      // As a last resort, update localStorage directly for the login source
      if (!loginSourceIncreased && typeof window !== 'undefined' && window.localStorage) {
        try {
          console.log("Manually updating login source in localStorage");
          const userUid = currentUser.uid;
          
          // Try to get existing wallet data
          const walletsStr = localStorage.getItem(`wallet_${userUid}`);
          const sourcesStr = localStorage.getItem(`coinSources_${userUid}`);
          
          // Parse existing data or create new objects
          let wallets = walletsStr ? JSON.parse(walletsStr) : { walletA: 0, walletB: 0 };
          let sources = sourcesStr ? JSON.parse(sourcesStr) : { ads: 0, referrals: 0, login: 0, accuracyBonus: 0 };
          
          // Update login source
          sources.login = (Number(sources.login) || 0) + rewardEstimate;
          
          // Ensure walletA reflects the update
          wallets.walletA = Math.max(Number(wallets.walletA) || 0, sources.ads + sources.login + sources.referrals + sources.accuracyBonus);
          
          // Save updated data back to localStorage
          localStorage.setItem(`wallet_${userUid}`, JSON.stringify(wallets));
          localStorage.setItem(`coinSources_${userUid}`, JSON.stringify(sources));
          
          // Final refresh to pull from localStorage
          await refreshWallet();
        } catch (error) {
          console.error("Error during manual localStorage update:", error);
        }
      }
      
      // After all refreshes are done, call the callback
      if (onRewardClaimed) {
        onRewardClaimed();
      }
    } catch (error) {
      console.error("Error during multiple wallet refreshes:", error);
    }
  };

  const handleClaimReward = async () => {
    if (!currentUser || !canClaimToday || loading || !isInitialized) return;
    
    setLoading(true);
    try {
      // Double-check eligibility using localStorage first
      let canProceed = true;
      
      if (typeof window !== 'undefined' && window.localStorage) {
        const lastClaimStr = localStorage.getItem(`lastClaim_${currentUser.uid}`);
        if (lastClaimStr) {
          const lastClaim = new Date(parseInt(lastClaimStr));
          const now = new Date();
          const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          
          // If there's a localStorage record from today, user is not eligible
          if (lastClaim >= todayStart) {
            console.log(`User ${currentUser.uid} already claimed today according to localStorage`);
            canProceed = false;
          }
        }
      }
      
      // Verify with server if possible
      try {
        canProceed = await checkDailyRewardEligibility(currentUser.uid);
      } catch (error) {
        console.error("Error verifying eligibility with server, using localStorage check:", error);
        // Continue with localStorage check result if server check fails
      }
      
      if (!canProceed) {
        setCanClaimToday(false);
        alert("You've already claimed your daily reward today!");
        setLoading(false);
        return;
      }
      
      // Add daily login reward
      try {
        const result = await addDailyLoginReward(currentUser.uid);
        console.log("Daily reward claim result:", result);
        
        if (!result || !result.success) {
          throw new Error("Failed to add login reward");
        }
        
        // Update state
        setCanClaimToday(false);
        setClaimSuccess(true);
        
        // Set last claim date to now
        const now = new Date();
        setLastClaimDate(now);
        
        // Update localStorage directly to ensure it's set
        if (typeof window !== 'undefined' && window.localStorage) {
          try {
            localStorage.setItem(`lastClaim_${currentUser.uid}`, now.getTime().toString());
          } catch (error) {
            console.error("Error writing to localStorage:", error);
          }
        }
        
        // Set next claim time to midnight tomorrow
        const todayMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const tomorrowMidnight = new Date(todayMidnight);
        tomorrowMidnight.setDate(tomorrowMidnight.getDate() + 1);
        setNextClaimTime(tomorrowMidnight);
        
        // Show notification
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('login-reward', { 
            detail: { 
              message: `You received ${result.amount} coins for your daily login!`,
              amount: result.amount
            } 
          }));
        }
        
        // Do multiple wallet refreshes to ensure the balance is updated
        await refreshWalletMultipleTimes();
      } catch (error) {
        console.error("Error adding login reward:", error);
        throw error; // Re-throw to be caught by outer catch
      }
    } catch (error) {
      console.error("Error claiming daily login reward:", error);
      alert("Failed to claim reward. Please try again later.");
      
      // Try to refresh the wallet anyway
      try {
        await refreshWallet();
      } catch (refreshError) {
        console.error("Error refreshing wallet after claim error:", refreshError);
      }
    } finally {
      setLoading(false);
    }
  };

  const formatTimeUntilNextClaim = () => {
    if (!nextClaimTime) return 'Available tomorrow';
    
    const now = new Date();
    const diffMs = nextClaimTime - now;
    
    if (diffMs <= 0) {
      return 'Available now';
    }
    
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `Available in ${hours}h ${minutes}m`;
    } else {
      return `Available in ${minutes}m`;
    }
  };
  
  const formatLastClaimDate = () => {
    if (!lastClaimDate) return 'Never claimed';
    
    return lastClaimDate.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  // If wallet isn't initialized yet, show loading state
  if (!isInitialized) {
    return (
      <div className="flex justify-center">
        <button 
          disabled
          className="bg-gray-400 text-white px-6 py-2 rounded-lg flex items-center justify-center w-full md:w-auto"
        >
          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Loading wallet...
        </button>
      </div>
    );
  }
  
  // Render the button
  return (
    <div>
      {checking ? (
        <div className="flex justify-center">
          <button 
            disabled
            className="bg-gray-400 text-white px-6 py-2 rounded-lg flex items-center justify-center w-full md:w-auto"
          >
            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Checking...
          </button>
        </div>
      ) : canClaimToday ? (
        <div className="space-y-2">
          <div className="bg-white dark:bg-gray-800 p-3 rounded-lg border border-green-200 dark:border-green-800 mb-2">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Daily Login Reward</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600 dark:text-gray-400">Reward:</p>
                <p className="text-lg font-bold text-green-600 dark:text-green-400">{rewardEstimate} coins</p>
              </div>
            </div>
          </div>
          <button 
            onClick={handleClaimReward}
            disabled={loading}
            className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center justify-center w-full"
          >
            {loading ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Processing...
              </>
            ) : (
              <>
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v3.586L7.707 9.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 10.586V7z" clipRule="evenodd"></path>
                </svg>
                Claim Daily Reward
              </>
            )}
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="bg-gray-100 dark:bg-gray-700 p-3 rounded-lg border border-gray-200 dark:border-gray-600 mb-2">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Last Claimed: <span className="font-medium text-gray-700 dark:text-gray-300">{formatLastClaimDate()}</span></p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600 dark:text-gray-400">Next Reward:</p>
                <p className="text-lg font-bold text-green-600 dark:text-green-400">{rewardEstimate} coins</p>
              </div>
            </div>
          </div>
          <button 
            disabled
            className="bg-gray-400 text-white px-6 py-3 rounded-lg font-medium flex items-center justify-center w-full cursor-not-allowed"
          >
            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd"></path>
            </svg>
            {formatTimeUntilNextClaim()}
          </button>
        </div>
      )}
    </div>
  );
};

export default ClaimDailyLoginButton; 